import {debounce} from '@github/mini-throttle'
import type {Dispatch, PropsWithChildren} from 'react'
import {createContext, use, useEffect, useMemo, useRef} from 'react'

import {useEntitlement} from '../components/quota/EntitlementContext'
import {useTraceDispatchMaybe} from '../components/tracing/TraceProvider'
import {usePlugins} from '../plugin/ImmersivePluginsProvider'
import {CopilotChatManager} from './copilot-chat-manager'
import type {CopilotChatAction, CopilotChatState} from './copilot-chat-reducer'
import type {CopilotChatOrg} from './copilot-chat-types'
import {copilotFeatureFlags} from './copilot-feature-flags'
import {useGetChatState} from './ObservableChatContext'

const CopilotChatManagerContext = createContext<CopilotChatManager | null>(null)

export interface CopilotChatManagerProviderProps {
  apiURL: string
  state: CopilotChatState
  dispatch: Dispatch<CopilotChatAction>
  ssoOrganizations: CopilotChatOrg[]
  realIp?: string
  hasCEorCBAccess?: boolean
  apiVersion?: string
}

export function CopilotChatManagerProvider({
  apiURL,
  state,
  dispatch,
  ssoOrganizations,
  children,
  realIp,
  hasCEorCBAccess,
  apiVersion,
}: PropsWithChildren<CopilotChatManagerProviderProps>) {
  const getChatState = useGetChatState()
  const plugins = usePlugins()
  const traceDispatch = useTraceDispatchMaybe()
  const manager = useMemo(
    () =>
      new CopilotChatManager(
        dispatch,
        apiURL,
        ssoOrganizations,
        getChatState,
        realIp,
        undefined,
        hasCEorCBAccess,
        plugins,
        apiVersion,
        traceDispatch,
      ),
    [dispatch, apiURL, ssoOrganizations, getChatState, realIp, hasCEorCBAccess, plugins, apiVersion, traceDispatch],
  )
  const {licenseType} = useEntitlement()

  const selectedThreadIDRef = useRef(state.selectedThreadID)
  useEffect(() => {
    selectedThreadIDRef.current = state.selectedThreadID
  })

  useEffect(() => {
    const controller = new AbortController()

    const fetchContextPage = async () => {
      // Only fetch implicit context for new threads — skip if the user is in an existing thread
      if (selectedThreadIDRef.current) {
        return
      }

      const hash = window.location.hash
      const pathName = window.location.pathname
      const url = window.location.hash ? `${pathName}${hash}` : pathName
      const urlParts = url.slice(1).split('/')
      const owner = urlParts[0]
      const repo = urlParts[1]

      if (owner && repo) {
        await manager.fetchImplicitContext(url, owner, repo, true)
      } else if (copilotFeatureFlags.immersiveEmbeddedImplicitReferences) {
        await manager.fetchImplicitContext(url, '', '', true)
      }
    }

    const voidFetch = () => {
      void fetchContextPage()
    }

    const debouncedFetch = debounce(() => voidFetch(), 500)

    function watchHistoryEvents() {
      // eslint-disable-next-line @typescript-eslint/unbound-method, no-restricted-properties
      const {replaceState, pushState} = window.history

      // eslint-disable-next-line no-restricted-properties
      window.history.replaceState = function (...args) {
        // eslint-disable-next-line no-restricted-properties
        replaceState.apply(window.history, args)

        // Chat suggestions are based on the current page context. Since fetching a new context is
        // an async (and debounced) call, clear any existing suggestions first to reduce flicker.
        manager.clearSuggestions()

        window.dispatchEvent(new Event('replaceState'))
      }

      // pushState is called by the browser when the user navigates to a new page. We need to listen
      // for this event to clear and generates suggestions.
      // eslint-disable-next-line no-restricted-properties
      window.history.pushState = function (...args) {
        // eslint-disable-next-line no-restricted-properties
        pushState.apply(window.history, args)

        // Chat suggestions are based on the current page context. Since fetching a new context is
        // an async (and debounced) call, clear any existing suggestions first to reduce flicker.
        manager.clearSuggestions()

        window.dispatchEvent(new Event('pushState'))
      }

      window.addEventListener('popstate', debouncedFetch, {signal: controller.signal})
      window.addEventListener('replaceState', debouncedFetch, {signal: controller.signal})
      window.addEventListener('pushState', debouncedFetch, {signal: controller.signal})
    }

    const shouldFetchImplicitReferences = copilotFeatureFlags.immersiveEmbeddedImplicitReferences

    if (state.chatIsOpen) {
      voidFetch()
      watchHistoryEvents()
    } else if (shouldFetchImplicitReferences) {
      watchHistoryEvents()
    }
    return () => {
      controller.abort()
    }
  }, [manager, state.chatIsOpen, state.mode, licenseType])

  return <CopilotChatManagerContext value={manager}>{children}</CopilotChatManagerContext>
}

export function useChatManager() {
  const context = use(CopilotChatManagerContext)
  if (!context) {
    throw new Error('useChatManager must be used within a CopilotChatManagerProvider')
  }
  return context
}
